/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable max-lines */
import React, {
  useEffect,
  useMemo,
  useCallback,
  useImperativeHandle,
} from 'react'

import equals from 'ramda/src/equals'
import isEmpty from 'ramda/src/isEmpty'
import {
  useTable,
  useFilters,
  useGlobalFilter,
  usePagination,
  useExpanded,
  useFlexLayout,
  useSortBy,
  Row,
} from 'react-table'

import { GridProps, GridRef } from '@customTypes'
import { noop } from '_helpers/functions'
import { usePreviousValue } from '_hooks'

import {
  Header,
  Table,
  Wrapper,
  RowMapper,
  HeaderCell,
  HeaderRow,
  renderSkeletonRowLoader,
} from './components'
import { globalFilter, smartStateReducer } from './helpers'

interface GridComponent {
  <D extends object>(
    props: GridProps<D> & {
      ref?: React.RefAttributes<GridRef<D>>
    }
  ): JSX.Element | null
  displayName?: string
}

export const Grid = React.forwardRef(
  <D extends object>(
    props: GridProps<D>,
    ref?: React.ForwardedRef<GridRef<D>>
  ) => {
    const {
      pageIndex = 0,
      pageSize = 10,
      loaderRenderer = renderSkeletonRowLoader,
      defaultMaxLines = 2,
      columns,
      data,
      search,
      filter,
      isLoading,
      loaderConfig,
    } = props

    const memoizedFilter = useCallback(
      (rows: Row<D>[], _: unknown, filterValue: string) =>
        globalFilter(rows, filterValue),
      []
    )
    const memoizedData = useMemo(() => {
      const { defaultSortFn } = props

      if (data && defaultSortFn) {
        return [...data].sort(defaultSortFn)
      }

      return data
    }, [data])

    const {
      getTableProps,
      getTableBodyProps,
      headerGroups,
      gotoPage,
      prepareRow,
      page,
      setGlobalFilter,
      setFilter,
      setAllFilters,
      pageCount,
      toggleAllRowsExpanded,
      state,
      rows,
      setSortBy,
    } = useTable(
      {
        columns,
        initialState: {
          pageSize,
          pageIndex,
        },
        data: memoizedData,
        globalFilter: memoizedFilter,
        stateReducer: smartStateReducer,
        autoResetPage: false,
      },
      useFlexLayout,
      useFilters,
      useGlobalFilter,
      useSortBy,
      useExpanded,
      usePagination
    )
    const prevState = usePreviousValue(state)

    useEffect(() => {
      if (typeof pageIndex === 'number' && pageIndex !== state.pageIndex) {
        gotoPage(pageIndex)
      }
    }, [pageIndex])

    useEffect(() => {
      const { onFilter = noop, onSort = noop } = props

      const gridState = {
        ...state,
        pageCount,
        dataCount: page.length,
      }

      if (
        !isEmpty(state.expanded) &&
        (!equals(state.sortBy, prevState.sortBy) ||
          state.pageIndex !== prevState.pageIndex)
      ) {
        toggleAllRowsExpanded(false)
      }

      if (!equals(state.sortBy, prevState.sortBy)) {
        onSort(gridState)
      }

      if (
        !equals(state.filters, prevState.filters) ||
        state.globalFilter !== prevState.globalFilter
      ) {
        onFilter(gridState)
      }

      if (state.sortBy !== prevState.sortBy) {
        onSort(gridState)
      }
    }, [state])

    useEffect(() => {
      toggleAllRowsExpanded(false)
      gotoPage(0)

      if (search !== state.globalFilter) {
        setGlobalFilter(search)
      }
      if (filter) {
        const filterArr = Array.isArray(filter) ? filter : [filter]
        filterArr.forEach(({ column, value }) => setFilter(column, value))
      } else {
        setAllFilters([])
      }
    }, [filter, search])

    useImperativeHandle(ref, () => ({
      ClearSort() {
        setSortBy([])
      },
      data: rows,
    }))

    const rowMapperProps = {
      page,
      defaultMaxLines,
      prepareRow,
      toggleAllRowsExpanded,
      showColumnDividers: props.showColumnDividers,
      expandedRowRenderer: props.expandedRowRenderer,
    }

    return (
      <Wrapper>
        <Table role="table" {...getTableProps()}>
          <Header>
            {headerGroups.map(headerGroup => {
              const { key: headerRowKey, ...headerRowProps } =
                headerGroup.getHeaderGroupProps()

              return (
                <HeaderRow key={headerRowKey} {...headerRowProps}>
                  {headerGroup.headers.map(column => {
                    const { key: headerKey, ...headerPros } =
                      column.getHeaderProps(column.getSortByToggleProps())

                    return (
                      <HeaderCell
                        key={headerKey}
                        isSorted={column.isSorted}
                        isSortedDesc={column.isSortedDesc}
                        isDisabled={isLoading}
                        {...headerPros}
                      >
                        {column.render('Header')}
                      </HeaderCell>
                    )
                  })}
                </HeaderRow>
              )
            })}
          </Header>
          <tbody {...getTableBodyProps()}>
            {isLoading ? (
              loaderRenderer(loaderConfig)
            ) : (
              <RowMapper {...rowMapperProps} />
            )}
          </tbody>
        </Table>
      </Wrapper>
    )
  }
) as GridComponent

Grid.displayName = 'Grid'
